/**
 * What is inside a storage unit, as an outline for the room view (z8nrz7fw25):
 * its zones, racks and boxes, two levels down, each with how many places it
 * holds. Positions themselves are left out — a freezer can have thousands —
 * and only counted. Pure, so the shape of the outline is tested.
 */
import type { ApiStorageNode } from '@/types/storage'
import { OCCUPIABLE_KIND } from '@/types/storage'

export interface OutlineNode {
  id: string
  kind: string
  label: string
  /** Positions anywhere beneath it. */
  positions: number
  /** Of those, how many hold something. */
  used: number
  children: OutlineNode[]
}

export function storageOutline(nodes: ApiStorageNode[], rootId: string, levels = 2): OutlineNode[] {
  const byParent = new Map<string, ApiStorageNode[]>()
  for (const n of nodes) {
    if (!n.parentId) continue
    byParent.set(n.parentId, [...(byParent.get(n.parentId) ?? []), n])
  }
  const count = (id: string): { positions: number; used: number } => {
    let positions = 0
    let used = 0
    for (const c of byParent.get(id) ?? []) {
      if (c.kind === OCCUPIABLE_KIND) {
        positions++
        if (c.occupied) used++
      } else {
        const sub = count(c.id)
        positions += sub.positions
        used += sub.used
      }
    }
    return { positions, used }
  }
  const build = (id: string, depth: number): OutlineNode[] =>
    (byParent.get(id) ?? [])
      .filter((c) => c.kind !== OCCUPIABLE_KIND)
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
      .map((c) => ({
        id: c.id,
        kind: c.kind,
        label: c.name ? `${c.code} · ${c.name}` : c.code,
        ...count(c.id),
        children: depth + 1 < levels ? build(c.id, depth + 1) : [],
      }))
  return build(rootId, 0)
}
